import axios, { AxiosError } from 'axios';
import { useAuthStore } from '../store/auth';

// Create axios instance with base URL
const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  timeout: 300000, // 5 minutes global timeout
});

// Flag to prevent multiple auth errors from showing
let isRefreshing = false;
let refreshPromise: Promise<string | null> | null = null;
const failedQueue: { resolve: (token: string) => void; reject: (error: any) => void }[] = [];

// Process the failed requests queue
const processQueue = (token: string | null, error: any = null) => {
  failedQueue.forEach(promise => {
    if (token) {
      promise.resolve(token);
    } else {
      promise.reject(error);
    }
  });
  failedQueue.length = 0;
};

// Function to attempt token refresh
async function attemptTokenRefresh() {
  if (!refreshPromise) {
    refreshPromise = useAuthStore.getState().refreshAccessToken();
  }

  try {
    const newToken = await refreshPromise;
    if (newToken) {
      processQueue(newToken);
      return newToken;
    }
    throw new Error('Token refresh failed');
  } catch (error) {
    processQueue(null, error);
    throw error;
  } finally {
    isRefreshing = false;
    refreshPromise = null;
  }
}

// Add request interceptor
api.interceptors.request.use(
  async config => {
    const authStore = useAuthStore.getState();
    const token = authStore.token;

    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  error => Promise.reject(error)
);

// Add response interceptor
api.interceptors.response.use(
  response => response,
  async (error: AxiosError) => {
    const originalRequest = error.config;

    // If error is 401 and we're not already refreshing
    if (error.response?.status === 401 && !isRefreshing && originalRequest) {
      isRefreshing = true;

      try {
        const newToken = await attemptTokenRefresh();
        if (newToken && originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return axios(originalRequest);
        }
      } catch (refreshError) {
        return Promise.reject(refreshError);
      }
    }

    // If error is 401 and we're already refreshing, queue the request
    if (error.response?.status === 401 && isRefreshing && originalRequest) {
      return new Promise((resolve, reject) => {
        failedQueue.push({
          resolve: (token: string) => {
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              resolve(axios(originalRequest));
            }
          },
          reject,
        });
      });
    }

    return Promise.reject(error);
  }
);

export default api;
